import * as d3 from 'd3';
import {Edge} from 'reactflow';

import {assert} from 'Asserts';
import {StructureGraphNodeType as StructureGraphNodeTypeGQL} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getWidthForBaseCount} from 'modbio/polymer-maps/OligoMap';

import {
  ComplexPolymerConnection,
  ComplexPolymerType,
  OLIGO_DUPLEX_FORWARD_STRAND_REF,
  OLIGO_DUPLEX_REVERSE_STRAND_REF,
  SimplePolymerGraphQL,
  StructureGraphEdgeGraphQL,
  StructureGraphGraphQL,
  StructureGraphNodeGraphQL,
} from '../ComplexPolymerConstants';
import {isForwardStrand, isPolymerBackboneStrand} from '../ComplexPolymerUtil';
import {
  BaseStructureGraphNode,
  EdgeType,
  NodeType,
  NucleotideStructureGraphNode,
  StructureGraphEdge,
  StructureGraphNode,
  Position,
  HandleType,
  BackendStructureGraphNode,
  NucleotideStructureGraphNodeData,
  BackendStructureGraphEdge,
  PolymerStructureGraph,
} from '../PolymerStructureGraphConstants';
import {ConjugateConnection} from '../tabs/ComplexPolymerComponentsSummaryUtil';

function getEdgesForNode(nodeId: string, allEdges: Array<StructureGraphEdge>): Array<StructureGraphEdge> {
  return allEdges.filter((edge) => edge.source === nodeId || edge.target === nodeId);
}

export function getActiveHandlesForNode(nodeId: string, edges: Array<StructureGraphEdge>): Array<HandleType> {
  const activeEdges = getEdgesForNode(nodeId, edges);
  const activeHandles: Array<HandleType> = [];
  activeEdges.forEach((edge) => {
    if (edge.source === nodeId) {
      activeHandles.push(edge.sourceHandle! as HandleType);
    } else if (edge.target === nodeId) {
      activeHandles.push(edge.targetHandle! as HandleType);
    }
  });

  return activeHandles;
}

export function getxFn(
  domainStart: number,
  domainEnd: number,
  rangeStart: number,
  rangeEnd: number,
  isAscending: boolean
): d3.ScaleLinear<number, number> {
  if (isAscending) {
    return d3.scaleLinear().clamp(true).domain([domainStart, domainEnd]).range([rangeStart, rangeEnd]);
  }
  return d3.scaleLinear().clamp(true).domain([domainStart, domainEnd]).range([rangeEnd, rangeStart]);
}

export function transformStructureGraphNodeForCanvas(
  complexPolymerType: ComplexPolymerType,
  nodes: Array<StructureGraphNodeGraphQL>
): Array<StructureGraphNode> {
  return nodes.map((node) => {
    if (node.type === StructureGraphNodeTypeGQL.NUCLEOTIDE) {
      return {
        ...node,
        type: NodeType.NUCLEOTIDE_NODE,
        data: {
          ...node.data,
          isForward: isForwardStrand(complexPolymerType, node.data.polymerRef),
          // Width and height of the node will be set once the canvas and nodes are rendered
          width: 0,
          height: 0,
        },
      } as NucleotideStructureGraphNode;
    }
    return {
      ...node,
      type: NodeType.CONJUGATE_NODE,
      data: {
        ...node.data,
        // Width and height of the node will be set once the canvas and nodes are rendered
        width: 0,
        height: 0,
      },
    } as BaseStructureGraphNode;
  });
}

export function transformStructureGraphEdgeForCanvas(
  edges: Array<StructureGraphEdgeGraphQL>
): Array<StructureGraphEdge> {
  return edges.map((edge) => ({
    ...edge,
    type: EdgeType.SMOOTHSTEP,
    source: edge.sourceId,
    sourceHandle: edge.sourceHandle,
    target: edge.targetId,
    targetHandle: edge.targetHandle,
  }));
}

export function transformStructureGraphForCanvas(
  complexPolymerType: ComplexPolymerType,
  structureGraph: StructureGraphGraphQL | null
): PolymerStructureGraph | null {
  if (structureGraph == null) {
    return null;
  }

  return {
    nodes: transformStructureGraphNodeForCanvas(complexPolymerType, structureGraph.nodes),
    edges: transformStructureGraphEdgeForCanvas(structureGraph.edges),
  };
}

export const OLIGO_CONJUGATE_STRAND_Y_POSITION = 40;
export const OLIGO_DUPLEX_FORWARD_STRAND_Y_POSITION = -35;
export const OLIGO_DUPLEX_REVERSE_STRAND_Y_POSITION = 85;

export interface NucleotideAxisData {
  id: string;
  position: Position;
  basesLength: number;
  isForward: boolean;
}

export function getNucleotideAxisDatas(
  complexPolymerType: ComplexPolymerType,
  simplePolymers: Array<SimplePolymerGraphQL>,
  forwardOffset: number = 0
): Array<NucleotideAxisData> {
  return simplePolymers
    .filter((polymer) => isPolymerBackboneStrand(complexPolymerType, polymer.helmReference))
    .map((polymer) => {
      const isReverse =
        complexPolymerType === ComplexPolymerType.OLIGO_DUPLEX &&
        polymer.helmReference === OLIGO_DUPLEX_REVERSE_STRAND_REF;
      let yPosition = 0;
      if (complexPolymerType === ComplexPolymerType.OLIGO_CONJUGATE) {
        yPosition = OLIGO_CONJUGATE_STRAND_Y_POSITION;
      } else {
        yPosition =
          polymer.helmReference === OLIGO_DUPLEX_FORWARD_STRAND_REF
            ? OLIGO_DUPLEX_FORWARD_STRAND_Y_POSITION
            : OLIGO_DUPLEX_REVERSE_STRAND_Y_POSITION;
      }
      assert(polymer.entity.__typename === 'SequenceNode', 'Expected sequence entity for backbone polymer');
      return {
        id: `${polymer.helmReference}_axis`,
        position: {
          x: isReverse ? -getWidthForBaseCount(forwardOffset) : 0,
          y: yPosition,
        },
        basesLength: polymer.entity.bases.length,
        isForward: !isReverse,
      };
    });
}

export function transformStructureGraphNodeForBackend(
  nodes: Array<StructureGraphNode>
): Array<BackendStructureGraphNode> {
  return nodes.map((node) => ({
    id: node.id,
    x: node.position.x,
    y: node.position.y,
    polymer_ref: node.data.polymerRef,
    residue_index: node.data.hasOwnProperty('residueIndex')
      ? (node.data as NucleotideStructureGraphNodeData).residueIndex
      : null,
  }));
}

export function transformStructureGraphEdgeForBackend(
  edges: Array<StructureGraphEdge>
): Array<BackendStructureGraphEdge> {
  return edges.map((edge) => ({
    id: edge.id,
    source: {
      id: edge.source,
      handle_type: edge.sourceHandle! as HandleType,
    },
    target: {
      id: edge.target,
      handle_type: edge.targetHandle! as HandleType,
    },
  }));
}

export function getNodeById(nodeId: string, nodes: Array<StructureGraphNode>): StructureGraphNode {
  return nodes.find((node) => node.id === nodeId)!;
}

export function doesConnectionMatchConjugateConnection(
  sourcePolymerRef: string,
  targetPolymerRef: string,
  conjugateConnection: ConjugateConnection
): boolean {
  return (
    (conjugateConnection.helmReference === sourcePolymerRef &&
      conjugateConnection.linkedEntityHelmReference === targetPolymerRef) ||
    (conjugateConnection.helmReference === targetPolymerRef &&
      conjugateConnection.linkedEntityHelmReference === sourcePolymerRef)
  );
}

export function doesEdgeMatchConjugateConnection(
  edge: Edge,
  nodes: Array<StructureGraphNode>,
  conjugateConnection: ConjugateConnection
): boolean {
  const sourceNode = getNodeById(edge.source, nodes);
  const targetNode = getNodeById(edge.target, nodes);
  const sourcePolymerRef = sourceNode.data.polymerRef;
  const targetPolymerRef = targetNode.data.polymerRef;

  return doesConnectionMatchConjugateConnection(sourcePolymerRef, targetPolymerRef, conjugateConnection);
}

export function doesEdgeMatchComplexPolymerConnection(
  edge: Edge,
  nodes: Array<StructureGraphNode>,
  connection: ComplexPolymerConnection
): boolean {
  const sourceNode = getNodeById(edge.source, nodes);
  const targetNode = getNodeById(edge.target, nodes);
  const sourcePolymerRef = sourceNode.data.polymerRef;
  const targetPolymerRef = targetNode.data.polymerRef;

  return (
    (connection.source.polymer_ref === sourcePolymerRef &&
      connection.target.polymer_ref === targetPolymerRef) ||
    (connection.source.polymer_ref === targetPolymerRef && connection.target.polymer_ref === sourcePolymerRef)
  );
}
